import type { CompensationMethod } from "@redotech/redo-return-core-common/compensation-method";
import {
  BLUE_YONDER_PROVIDER,
  DEFAULT_DROPOFF_RADIUS_MILES,
  dropoffProviderType,
  HAPPY_RETURNS_PROVIDER,
  RETURN_BEAR_PROVIDER,
  VIRTUAL_PROVIDER,
  type DropoffProviderType,
} from "@redotech/redo-return-dropoff-provider-common/dropoff-provider";
import * as z from "zod";
import {
  DROPOFF_ENABLED_DEFAULT,
  mergeDropoffEnabledAndRadius,
} from "./dropoff-settings-merge";
import {
  changedTeamPatchPaths,
  groupedSettingsChanges,
  sameSettingsValue,
} from "./grouped-settings-changes";
import type { DropoffProviderSettings } from "./returns-settings";

export const RETURN_LOCATION_RADIUS_MAX_MILES = 500;
const DEFAULT_IN_STORE_RADIUS_MILES = 100;

const radiusMiles = z
  .number()
  .int()
  .min(1)
  .max(RETURN_LOCATION_RADIUS_MAX_MILES);

export const InStoreAddressSchema = z.object({
  /** Kept so an unchanged address keeps its id when the list is rewritten. */
  _id: z.string().optional(),
  name: z.string(),
  street1: z.string(),
  street2: z.string().nullish(),
  city: z.string(),
  state: z.string().nullish(),
  zip: z.string(),
  country: z.string(),
  country_name: z.string().nullish(),
  phone: z.string().nullish(),
  latitude: z.number().nullish(),
  longitude: z.number().nullish(),
});

export type InStoreAddress = z.infer<typeof InStoreAddressSchema>;

const DropoffSchema = z.object({ enabled: z.boolean(), radiusMiles });

export const ReturnsLocationsSettingsSchema = z.object({
  inStore: z.object({
    enabled: z.boolean(),
    radiusMiles,
    customAddresses: z.array(InStoreAddressSchema),
  }),
  dropoffs: z.object({
    [VIRTUAL_PROVIDER]: DropoffSchema,
    [RETURN_BEAR_PROVIDER]: DropoffSchema,
    [BLUE_YONDER_PROVIDER]: DropoffSchema,
    [HAPPY_RETURNS_PROVIDER]: DropoffSchema,
  }),
});

export type ReturnsLocationsSettings = z.infer<
  typeof ReturnsLocationsSettingsSchema
>;

const settingsShape = ReturnsLocationsSettingsSchema.shape;

/** Only the fields a merchant changed; everything omitted is left as stored. */
export const ReturnsLocationsSettingsChangesSchema = z.object({
  inStore: settingsShape.inStore.partial().optional(),
  dropoffs: settingsShape.dropoffs.partial().optional(),
});

export type ReturnsLocationsSettingsChanges = z.infer<
  typeof ReturnsLocationsSettingsChangesSchema
>;

export function returnsLocationsSettingsChanges(
  initial: ReturnsLocationsSettings,
  next: ReturnsLocationsSettings,
): ReturnsLocationsSettingsChanges {
  return ReturnsLocationsSettingsChangesSchema.parse(
    groupedSettingsChanges(initial, next),
  );
}

/** Not re-validated: a legacy stored value that fails today's schema must not block saving an unrelated field. */
function applyReturnsLocationsSettingsChanges(
  base: ReturnsLocationsSettings,
  changes: ReturnsLocationsSettingsChanges,
): ReturnsLocationsSettings {
  return {
    inStore: { ...base.inStore, ...changes.inStore },
    dropoffs: { ...base.dropoffs, ...changes.dropoffs },
  };
}

interface StoredInStoreAddress {
  _id?: { toString(): string } | null;
  name: string;
  street1: string;
  street2?: string | null;
  city: string;
  state?: string | null;
  zip: string;
  country: string;
  country_name?: string | null;
  phone?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export interface StoredDropoff {
  provider: DropoffProviderType;
  enabled?: boolean | null;
  radiusMiles?: number | null;
  eligibleAutoProcessCompensationMethods?: CompensationMethod[] | null;
}

/** The slice of the team the Locations tab reads. Structurally matches both the client team and the server's raw document. */
export interface ReturnsLocationsSettingsSource {
  settings: {
    returnInStoreEnabled?: boolean | null;
    returnInStoreRadiusMiles?: number | null;
    returns?: { dropoffs?: StoredDropoff[] | null } | null;
  };
  customInStoreAddresses?: StoredInStoreAddress[] | null;
}

function inStoreAddressFromTeam(address: StoredInStoreAddress): InStoreAddress {
  return {
    ...(address._id ? { _id: address._id.toString() } : {}),
    name: address.name,
    street1: address.street1,
    street2: address.street2,
    city: address.city,
    state: address.state,
    zip: address.zip,
    country: address.country,
    country_name: address.country_name,
    phone: address.phone,
    latitude: address.latitude,
    longitude: address.longitude,
  };
}

export function returnsLocationsSettingsFromTeam({
  settings,
  customInStoreAddresses,
}: ReturnsLocationsSettingsSource): ReturnsLocationsSettings {
  const dropoffFromTeam = (provider: DropoffProviderType) => {
    const stored = settings.returns?.dropoffs?.find(
      (dropoff) => dropoff.provider === provider,
    );
    return {
      enabled: stored?.enabled ?? DROPOFF_ENABLED_DEFAULT,
      radiusMiles: stored?.radiusMiles ?? DEFAULT_DROPOFF_RADIUS_MILES,
    };
  };
  return {
    inStore: {
      enabled: !!settings.returnInStoreEnabled,
      radiusMiles:
        settings.returnInStoreRadiusMiles ?? DEFAULT_IN_STORE_RADIUS_MILES,
      customAddresses: (customInStoreAddresses ?? []).map(
        inStoreAddressFromTeam,
      ),
    },
    dropoffs: {
      [VIRTUAL_PROVIDER]: dropoffFromTeam(VIRTUAL_PROVIDER),
      [RETURN_BEAR_PROVIDER]: dropoffFromTeam(RETURN_BEAR_PROVIDER),
      [BLUE_YONDER_PROVIDER]: dropoffFromTeam(BLUE_YONDER_PROVIDER),
      [HAPPY_RETURNS_PROVIDER]: dropoffFromTeam(HAPPY_RETURNS_PROVIDER),
    },
  };
}

export interface ReturnsLocationsTeamPatch {
  /** Dotted paths relative to `team.settings`. */
  set: Record<string, unknown>;
  unset: string[];
  /** Top-level team fields; absent when unchanged. */
  customInStoreAddresses?: InStoreAddress[];
}

/** Stored nulls read as unset, matching how the Shipping page loads them. */
export function dropoffSettingsFromStored(
  dropoff: StoredDropoff,
): DropoffProviderSettings {
  return {
    ...dropoff,
    enabled: dropoff.enabled ?? undefined,
    radiusMiles: dropoff.radiusMiles ?? DEFAULT_DROPOFF_RADIUS_MILES,
    eligibleAutoProcessCompensationMethods:
      dropoff.eligibleAutoProcessCompensationMethods ?? undefined,
  };
}

function returnsLocationsSettingsToTeamPatch(
  current: ReturnsLocationsSettingsSource,
  next: ReturnsLocationsSettings,
): { set: Record<string, unknown>; unset: string[] } {
  return {
    set: {
      returnInStoreEnabled: next.inStore.enabled,
      returnInStoreRadiusMiles: next.inStore.radiusMiles,
      "returns.dropoffs": mergeDropoffEnabledAndRadius(
        current.settings.returns?.dropoffs?.map(dropoffSettingsFromStored),
        dropoffProviderType.map((provider) => ({
          provider,
          ...next.dropoffs[provider],
        })),
      ),
    },
    unset: [],
  };
}

/**
 * Writes only what the merchant changed. Untouched fields are rebuilt from the
 * stored settings, so a stale form can't revert edits made elsewhere.
 */
export function returnsLocationsTeamPatchForChanges(
  current: ReturnsLocationsSettingsSource,
  changes: ReturnsLocationsSettingsChanges,
): ReturnsLocationsTeamPatch {
  const storedValue = returnsLocationsSettingsFromTeam(current);
  const next = applyReturnsLocationsSettingsChanges(storedValue, changes);
  const patch: ReturnsLocationsTeamPatch = changedTeamPatchPaths(
    returnsLocationsSettingsToTeamPatch(current, storedValue),
    returnsLocationsSettingsToTeamPatch(current, next),
  );
  if (
    !sameSettingsValue(
      storedValue.inStore.customAddresses,
      next.inStore.customAddresses,
    )
  ) {
    patch.customInStoreAddresses = next.inStore.customAddresses;
  }
  return patch;
}
